import React, { useState, useEffect } from 'react';
import { GlassCard, GlassButton, GlassBadge } from '@/design-system';
import { Input, Select } from '@/components/ui';
import {
  ChevronRight,
  ChevronLeft,
  Layers,
  Building2,
  FileCode,
  Target,
  Check,
  Link2,
} from 'lucide-react';
import type { Objective, InvestibleStructure, Program, Epic, OKRLevel, OKRStatusNew } from '@/types';

interface OKRFormProps {
  initialData?: Partial<Objective>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  investibleStructures: InvestibleStructure[];
  programs: Program[];
  epics: Epic[];
  existingObjectives?: Objective[];
  defaultFiscalYear?: number;
  defaultQuarter?: number;
}

type FormStep = 'level' | 'entity' | 'cycle' | 'details' | 'alignment';

const STEPS: { id: FormStep; label: string }[] = [
  { id: 'level', label: 'Level' },
  { id: 'entity', label: 'Entity' },
  { id: 'cycle', label: 'Cycle' },
  { id: 'details', label: 'Details' },
  { id: 'alignment', label: 'Alignment' },
];

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const QUARTER_OPTIONS = [
  { value: '1', label: 'Q1 (Oct - Dec)' },
  { value: '2', label: 'Q2 (Jan - Mar)' },
  { value: '3', label: 'Q3 (Apr - Jun)' },
  { value: '4', label: 'Q4 (Jul - Sep)' },
];

const getCurrentFiscalYear = () => {
  const now = new Date();
  const month = now.getMonth();
  return month >= 9 ? now.getFullYear() + 1 : now.getFullYear();
};

const OKRForm: React.FC<OKRFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  investibleStructures,
  programs,
  epics,
  existingObjectives = [],
  defaultFiscalYear,
  defaultQuarter,
}) => {
  const isEditing = !!initialData?.id;

  // Determine initial level from initialData
  const getInitialLevel = (): OKRLevel | null => {
    if (initialData?.investibleStructureId) return 'IS';
    if (initialData?.programId) return 'PROGRAM';
    if (initialData?.epicId) return 'EPIC';
    return null;
  };

  // Form state
  const [currentStep, setCurrentStep] = useState<FormStep>(isEditing ? 'details' : 'level');
  const [selectedLevel, setSelectedLevel] = useState<OKRLevel | null>(getInitialLevel());
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    investibleStructureId: initialData?.investibleStructureId || '',
    programId: initialData?.programId || '',
    epicId: initialData?.epicId || '',
    quarter: initialData?.quarter?.toString() || defaultQuarter?.toString() || '',
    fiscalYear: initialData?.fiscalYear?.toString() || defaultFiscalYear?.toString() || getCurrentFiscalYear().toString(),
    status: initialData?.status || 'DRAFT',
    parentObjectiveId: initialData?.parentObjectiveId || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Generate fiscal year options
  const fiscalYearOptions = Array.from({ length: 7 }, (_, i) => ({
    value: (getCurrentFiscalYear() - 3 + i).toString(),
    label: `FY${(getCurrentFiscalYear() - 3 + i).toString().slice(-2)}`,
  }));

  // Entity options based on selected level
  const getEntityOptions = () => {
    switch (selectedLevel) {
      case 'IS':
        return investibleStructures.map((is) => ({
          value: is.id,
          label: `${is.name} (${is.category})`,
        }));
      case 'PROGRAM':
        return programs.map((p) => ({
          value: p.id,
          label: p.name,
        }));
      case 'EPIC':
        return epics.map((e) => ({
          value: e.id,
          label: `${e.name} (${e.status})`,
        }));
      default:
        return [];
    }
  };

  // Get potential parent objectives based on level
  const getParentObjectiveOptions = () => {
    if (!selectedLevel) return [];

    // IS-level objectives can't have parents (they're top-level)
    if (selectedLevel === 'IS') return [];

    // Program-level objectives can align to IS objectives
    if (selectedLevel === 'PROGRAM') {
      return existingObjectives
        .filter((obj) => obj.level === 'IS' && obj.id !== initialData?.id)
        .map((obj) => ({
          value: obj.id,
          label: `[IS] ${obj.title}`,
        }));
    }

    // EPIC-level objectives can align to IS or Program objectives
    if (selectedLevel === 'EPIC') {
      return existingObjectives
        .filter((obj) => (obj.level === 'IS' || obj.level === 'PROGRAM') && obj.id !== initialData?.id)
        .map((obj) => ({
          value: obj.id,
          label: `[${obj.level}] ${obj.title}`,
        }));
    }

    return [];
  };

  // Get selected entity name for display
  const getSelectedEntityName = () => {
    switch (selectedLevel) {
      case 'IS':
        return investibleStructures.find((is) => is.id === formData.investibleStructureId)?.name;
      case 'PROGRAM':
        return programs.find((p) => p.id === formData.programId)?.name;
      case 'EPIC':
        return epics.find((e) => e.id === formData.epicId)?.name;
      default:
        return '';
    }
  };

  // Get the current entity value
  const getCurrentEntityValue = () => {
    switch (selectedLevel) {
      case 'IS':
        return formData.investibleStructureId;
      case 'PROGRAM':
        return formData.programId;
      case 'EPIC':
        return formData.epicId;
      default:
        return '';
    }
  };

  // Set entity value based on level
  const setEntityValue = (value: string) => {
    switch (selectedLevel) {
      case 'IS':
        setFormData((prev) => ({ ...prev, investibleStructureId: value, programId: '', epicId: '' }));
        break;
      case 'PROGRAM':
        setFormData((prev) => ({ ...prev, programId: value, investibleStructureId: '', epicId: '' }));
        break;
      case 'EPIC':
        setFormData((prev) => ({ ...prev, epicId: value, investibleStructureId: '', programId: '' }));
        break;
    }
  };

  // Validation
  const validateStep = (step: FormStep): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 'level':
        if (!selectedLevel) {
          newErrors.level = 'Please select a level';
        }
        break;
      case 'entity':
        if (!getCurrentEntityValue()) {
          newErrors.entity = 'Please select an entity';
        }
        break;
      case 'cycle':
        if (!formData.quarter) {
          newErrors.quarter = 'Quarter is required';
        }
        if (!formData.fiscalYear) {
          newErrors.fiscalYear = 'Fiscal year is required';
        }
        break;
      case 'details':
        if (!formData.title.trim()) {
          newErrors.title = 'Title is required';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const goToNextStep = () => {
    if (!validateStep(currentStep)) return;

    const currentIndex = STEPS.findIndex((s) => s.id === currentStep);
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1].id);
    }
  };

  const goToPreviousStep = () => {
    const currentIndex = STEPS.findIndex((s) => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].id);
    }
  };

  // Submit
  const handleSubmit = () => {
    if (!validateStep('details')) return;

    const submitData: any = {
      title: formData.title,
      description: formData.description || undefined,
      quarter: parseInt(formData.quarter),
      fiscalYear: parseInt(formData.fiscalYear),
      status: formData.status,
      parentObjectiveId: formData.parentObjectiveId || undefined,
    };

    // Add the appropriate entity ID based on level
    switch (selectedLevel) {
      case 'IS':
        submitData.investibleStructureId = formData.investibleStructureId;
        break;
      case 'PROGRAM':
        submitData.programId = formData.programId;
        break;
      case 'EPIC':
        submitData.epicId = formData.epicId;
        break;
    }

    onSubmit(submitData);
  };

  const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep);
  const isLastStep = currentStepIndex === STEPS.length - 1;

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      {!isEditing && (
        <div className="flex items-center justify-center gap-2">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                  index < currentStepIndex
                    ? 'bg-green-500 text-white'
                    : index === currentStepIndex
                    ? 'bg-blue-500 text-white'
                    : 'bg-white/10 text-gray-400'
                }`}
              >
                {index < currentStepIndex ? <Check className="w-4 h-4" /> : index + 1}
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`w-12 h-0.5 ${
                    index < currentStepIndex ? 'bg-green-500' : 'bg-white/10'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Step Content */}
      <div className="min-h-[300px]">
        {/* Level Selection Step */}
        {currentStep === 'level' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground text-center mb-6">
              Select Objective Level
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {(['IS', 'PROGRAM', 'EPIC'] as OKRLevel[]).map((level) => {
                const isSelected = selectedLevel === level;
                const Icon = level === 'IS' ? Layers : level === 'PROGRAM' ? Building2 : FileCode;
                const color = level === 'IS' ? 'purple' : level === 'PROGRAM' ? 'blue' : 'green';
                const description =
                  level === 'IS'
                    ? 'Strategic objectives for Investible Structures'
                    : level === 'PROGRAM'
                    ? 'Operational objectives for Programs'
                    : 'Tactical objectives for EPICs';

                return (
                  <button
                    key={level}
                    onClick={() => setSelectedLevel(level)}
                    className={`p-6 rounded-xl border-2 transition-all ${
                      isSelected
                        ? `border-${color}-500 bg-${color}-500/10`
                        : 'border-[var(--border-primary)] hover:border-white/30 hover:bg-[var(--bg-hover)]/50'
                    }`}
                  >
                    <div
                      className={`p-3 rounded-lg mx-auto w-fit ${
                        isSelected ? `bg-${color}-500/20` : 'bg-[var(--bg-tertiary)]/50'
                      }`}
                    >
                      <Icon className={`w-8 h-8 ${isSelected ? `text-${color}-400` : 'text-muted-foreground'}`} />
                    </div>
                    <h4 className="text-foreground font-medium mt-4">{level} Level</h4>
                    <p className="text-sm text-muted-foreground mt-2">{description}</p>
                  </button>
                );
              })}
            </div>
            {errors.level && <p className="text-sm text-status-error text-center">{errors.level}</p>}
          </div>
        )}

        {/* Entity Selection Step */}
        {currentStep === 'entity' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground text-center mb-6">
              Select {selectedLevel === 'IS' ? 'Investible Structure' : selectedLevel}
            </h3>
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {getEntityOptions().map((option) => (
                <button
                  key={option.value}
                  onClick={() => setEntityValue(option.value)}
                  className={`w-full p-4 text-left rounded-lg border transition-all ${
                    getCurrentEntityValue() === option.value
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-[var(--border-primary)] hover:border-white/30 hover:bg-[var(--bg-hover)]/50'
                  }`}
                >
                  <span className="text-foreground">{option.label}</span>
                </button>
              ))}
            </div>
            {errors.entity && <p className="text-sm text-status-error text-center">{errors.entity}</p>}
          </div>
        )}

        {/* Cycle Selection Step */}
        {currentStep === 'cycle' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground text-center mb-6">
              Select OKR Cycle
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Fiscal Year</label>
                <select
                  value={formData.fiscalYear}
                  onChange={(e) => setFormData((prev) => ({ ...prev, fiscalYear: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                >
                  {fiscalYearOptions.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-[var(--bg-elevated)]">
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.fiscalYear && <p className="text-sm text-status-error mt-1">{errors.fiscalYear}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Quarter</label>
                <select
                  value={formData.quarter}
                  onChange={(e) => setFormData((prev) => ({ ...prev, quarter: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                >
                  <option value="" className="bg-[var(--bg-elevated)]">Select quarter</option>
                  {QUARTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-[var(--bg-elevated)]">
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.quarter && <p className="text-sm text-status-error mt-1">{errors.quarter}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Details Step */}
        {currentStep === 'details' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white text-center mb-6">
              Objective Details
            </h3>

            {/* Summary badge for context */}
            {!isEditing && (
              <div className="flex items-center gap-2 justify-center mb-4">
                <GlassBadge variant="default">{selectedLevel} Level</GlassBadge>
                <GlassBadge variant="default">{getSelectedEntityName()}</GlassBadge>
                <GlassBadge variant="default">
                  Q{formData.quarter} FY{formData.fiscalYear.slice(-2)}
                </GlassBadge>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Objective Title <span className="text-red-400">*</span>
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="What do you want to achieve?"
                error={errors.title}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Provide more context about this objective (optional)"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-[var(--bg-elevated)]">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Alignment Step */}
        {currentStep === 'alignment' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white text-center mb-6">
              Align to Parent Objective
            </h3>

            {selectedLevel === 'IS' ? (
              <div className="text-center py-8">
                <Target className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">IS-level objectives are top-level</p>
                <p className="text-sm text-gray-500">They don't have parent objectives</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-400 text-center mb-4">
                  Optionally align this objective to a higher-level objective
                </p>
                <div className="max-h-[250px] overflow-y-auto space-y-2">
                  <button
                    onClick={() => setFormData((prev) => ({ ...prev, parentObjectiveId: '' }))}
                    className={`w-full p-4 text-left rounded-lg border transition-all ${
                      !formData.parentObjectiveId
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-white/10">
                        <Target className="w-4 h-4 text-gray-400" />
                      </div>
                      <span className="text-gray-300">No alignment (standalone)</span>
                    </div>
                  </button>

                  {getParentObjectiveOptions().map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFormData((prev) => ({ ...prev, parentObjectiveId: option.value }))}
                      className={`w-full p-4 text-left rounded-lg border transition-all ${
                        formData.parentObjectiveId === option.value
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/20">
                          <Link2 className="w-4 h-4 text-blue-400" />
                        </div>
                        <span className="text-white">{option.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t border-white/10">
        <div>
          {currentStepIndex > 0 && !isEditing && (
            <GlassButton variant="ghost" onClick={goToPreviousStep}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </GlassButton>
          )}
        </div>

        <div className="flex gap-3">
          <GlassButton variant="ghost" onClick={onCancel}>
            Cancel
          </GlassButton>
          {isLastStep || isEditing ? (
            <GlassButton variant="primary" onClick={handleSubmit}>
              {isEditing ? 'Save Changes' : 'Create Objective'}
            </GlassButton>
          ) : (
            <GlassButton variant="primary" onClick={goToNextStep}>
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </GlassButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default OKRForm;
